
import { Component, Mixins, Prop, Vue } from 'vue-property-decorator'
import { fabric } from 'fabric'
import opentype from 'opentype.js'
import { roundOff } from '@/helpers/Helpers'
import ErrorMessages from "@/mixins/ErrorMessages";

@Component<ProductionFileGenerator>({
  mounted() {
    this.loadScene()
  }
})
export default class ProductionFileGenerator extends Mixins(ErrorMessages) {
  @Prop({ required: true }) readonly designUrl!: string
  @Prop({ required: true }) readonly sizesUrl!: string
  @Prop({ required: true }) readonly safeZoneUrl!: string
  @Prop({ required: false, default: 2048 }) readonly canvasResolution!: number;
  @Prop({ required: false, default: 512 }) readonly containerWidth!: number;
  @Prop({ required: false, default: 512 }) readonly containerHeight!: number;
  @Prop({ required: false, default: 600 }) readonly twoDCanvasWidth!: number;
  @Prop({ required: false, default: 600 }) readonly twoDCanvasHeight!: number;
  @Prop({ required: true }) readonly groupPatterns!: Record<string, any>
  @Prop({ required: false, default: () => [] }) readonly logos!: Record<string, any>[]
  @Prop({ required: false, default: () => [] }) readonly custom_logos!: Record<string, any>[]
  @Prop({ required: false, default: () => [] }) readonly texts!: Record<string, any>[]
  @Prop({ required: false, default: () => [] }) readonly svgGroups!: Record<string, any>[]
  @Prop({ required: false, default: '' }) readonly sizeDownload!: string

  private canvas!: fabric.Canvas
  private design!: fabric.Group
  private size!: fabric.Group
  private mainSize!: fabric.Group
  private sizeSpec!: Record<string, any>
  private mainSizeSpec!: Record<string, any>
  private sizeDifferencesRatio!: Record<string, any>
  private safe_zone!: fabric.Group
  private storage_url = `${process.env.VUE_APP_FILE_URL}/`
  private patterns: Record<any, any> = {}
  private mainSizeId!: string

  get canvasWidthRatio() {
    return this.containerWidth / this.twoDCanvasWidth
  }
  get canvasHeightRatio() {
    return this.containerHeight / this.twoDCanvasHeight
  }

  // Helper methods moved from SceneMixin
  private cloneFabricObjects(objects: fabric.Object[]): fabric.Object[] {
    return objects.map(obj => fabric.util.object.clone(obj))
  }

  public async setupPattern(svg_part: string, pattern_data: any, design: fabric.Group, BASE_DESIGN_WIDTH = 6000): Promise<fabric.Pattern | null> {
    const pattern_url = pattern_data.pattern_url;
    return new Promise((resolve) => {
      fabric.loadSVGFromURL(`${this.storage_url}${pattern_url}?q=${pattern_data.random_string}`, (objects: any, options: any) => {
        if (!objects.length) {
          resolve(null);
          return;
        }

        options.crossOrigin = 'Anonymous';
        const img = fabric.util.groupSVGElements(objects) as fabric.Group;

        if (this.groupPatterns[svg_part].color?.value) {
          if (img._objects) {
            img.getObjects().forEach((obj) => {
              obj.set('fill', this.groupPatterns[svg_part].color.value)
            });
          } else {
            img.set('fill', this.groupPatterns[svg_part].color.value)
          }
        }

        const scale_value_received = parseInt(this.groupPatterns[svg_part].scale);
        const baseScaleRatio = design.width as number / BASE_DESIGN_WIDTH;
        const userScaleNormalized = scale_value_received / 100;
        const scaleFactor = baseScaleRatio * userScaleNormalized;

        img.scale(scaleFactor);

        const scaledWidth = img.getScaledWidth();
        const scaledHeight = img.getScaledHeight();

        const svgString = `<svg xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 ${scaledWidth} ${scaledHeight}"
          width="${scaledWidth}" 
          height="${scaledHeight}">
          ${img.toSVG()}
         </svg>`;

        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgString, "image/svg+xml");
        const svgElement = svgDoc.documentElement;

        const dataUrl = this.svgToDataURL(svgElement);

        const svg_image = new Image();
        svg_image.crossOrigin = "Anonymous";
        svg_image.src = dataUrl;

        const pattern = new fabric.Pattern({
          source: svg_image,
          repeat: 'repeat'
        });

        const rotationAngle = this.groupPatterns[svg_part]?.angle || 0;
        const angleInRadians = (rotationAngle * Math.PI) / 180;
        const cos = Math.cos(angleInRadians);
        const sin = Math.sin(angleInRadians);

        const patternWidth = img.getScaledWidth();
        const patternHeight = img.getScaledHeight();
        const tx = (patternWidth / 2) * (1 - cos) + (patternHeight / 2) * sin;
        const ty = (patternHeight / 2) * (1 - cos) - (patternWidth / 2) * sin;

        pattern.patternTransform = [
          cos,      // x-axis scale (1 for no scaling)
          sin,      // y-axis skew
          -sin,     // x-axis skew
          cos,      // y-axis scale (1 for no scaling)
          -tx,      // x translation
          -ty       // y translation
        ];

        resolve(pattern);
      });
    });
  }

  private async loadScene() {
    const element = this.$refs.canvas as HTMLCanvasElement
    this.canvas = new fabric.Canvas(element, {
      selection: false,
      interactive: false
    })

    await this.addSize('L', true)
    let sizs_found = await this.addSize(this.sizeDownload)
    if (!sizs_found) {
      this.$emit('resetDownloadProductionFile')
      this.showError(`Size ${this.sizeDownload} not found in the SVG`)

      return;
    }
    await this.calculateSizeDifferences()

    await this.addDesign()
    await this.downloadDesign()
  }

  private async downloadDesign() {
    let targetWidth = Number(this.design?.width) || this.canvasResolution
    let targetHeight = Number(this.design?.height) || this.canvasResolution
    const anchor = this.design?._objects?.find((obj: any) => {
      const id = obj?.id
      return typeof id === 'string' && id.toLowerCase().includes('anchor')
    })
    if (anchor?.width && anchor?.height) {
      targetWidth = anchor.width as number
      targetHeight = anchor.height as number
    }
    
    const svg = this.generateProductionSVG(targetWidth, targetHeight)
    const blob = new Blob([svg], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `design_${this.sizeDownload}.svg`
    a.click()
    URL.revokeObjectURL(url)
    this.$emit('resetDownloadProductionFile')
  }

  private async addFixedLogos() {
    if (this.logos && this.logos.length) {
      for (const logo of this.logos) {
        await this.addSvgLogo(logo)
      }
    }
  }
  public async addSvgLogo(logo: Record<any, any>): Promise<boolean> {
    return new Promise((resolve) => {
      let logoUrl = encodeURI((this.storage_url + logo.url).trim()) + '?nocache=11'
      fabric.loadSVGFromURL(logoUrl, (objects: any, options: any) => {
        options.crossOrigin = 'Anonymous'
        const img = fabric.util.groupSVGElements(objects) as fabric.Group
        img.set({
          left: logo.left,
          top: logo.top,
          scaleX: logo.scaleX ?? 1,
          scaleY: logo.scaleY ?? 1,
          angle: logo.angle ?? 0,
          hasControls: false,
          selectable: false,
          evented: false,
          lockMovementX: true,
          lockMovementY: true,
          globalCompositeOperation: 'source-atop',
          originX: 'center',
          originY: 'center',
          flipX: true,
          type: "logo",
        })

        if (logo.is_customizable) {
          const id = `${logo.placement_title}`
          const svgGroup = this.containsObject({ id: id })
          if (svgGroup) {
            const logo_objects = img._objects ? img._objects : [img]
            logo_objects.forEach((item: any) => {
              item.set({ fill: svgGroup.color });
            })
          }
        }

        this.canvas.add(img)
        this.canvas.requestRenderAll()
        resolve(true)
      })
    })
  }

  public containsObject(obj: Record<any, any>): Record<any, any> {
    for (let i = 0; i < this.svgGroups.length; i++) {
      if (this.svgGroups[i].id == obj.id) {
        return this.svgGroups[i]
      }
    }
    return {}
  }
  
  private async changeColors(design: fabric.Object[]) {
    if (this.svgGroups && this.svgGroups.length) {
      
      design.forEach((item: Record<any, any>) => {
        if (item.id) {
          const item_id = item.id.split('_')[0].toLowerCase()
          
          // Find matching SVG group
          const matchingSvgGroup = this.svgGroups.find(group => group.id === item_id);
          
          if (matchingSvgGroup) {
            if (item.fill && item.fill.gradientUnits) {
              // Handle gradient fill
              if (matchingSvgGroup.gradient_colors && matchingSvgGroup.gradient_colors.length) {
                // Update each color stop with the corresponding gradient color
                matchingSvgGroup.gradient_colors.forEach((gradientColor, index) => {
                  if (item.fill.colorStops[index]) {
                    item.fill.colorStops[index].color = gradientColor.color;
                  }
                });
                item.set('fill', new fabric.Gradient(item.fill));
              } else if (matchingSvgGroup.color) {
                // If no gradient colors but has a solid color, apply it to first color stop
                if (item.fill.colorStops[0]) {
                  item.fill.colorStops[0].color = matchingSvgGroup.color;
                  item.set('fill', new fabric.Gradient(item.fill));
                }
              }
            } else {
              // Handle solid color fill
              if (matchingSvgGroup.gradient_colors && matchingSvgGroup.gradient_colors.length) {
                // If gradient colors exist but item doesn't support gradient, use first color
                item.set('fill', matchingSvgGroup.gradient_colors[0].color);
              } else {
                // Use solid color
                item.set('fill', matchingSvgGroup.color);
              }
            }
          }
        }
      });
    }
  }

  public findMostLeftAndTopElements(design: fabric.Group) {
    if (!design || !design._objects) {
      return { maxLeft: 0, maxTop: 0 };
    }

    let maxLeft = Infinity;
    let maxTop = Infinity;

    design._objects.forEach((obj: any) => {
      const left = obj.left ?? 0;
      const top = obj.top ?? 0

      if (left < maxLeft) {
        maxLeft = left;
      }
      if (top < maxTop) {
        maxTop = top;
      }
    })

    return {
      maxLeft: maxLeft === Infinity ? 0 : maxLeft,
      maxTop: maxTop === Infinity ? 0 : maxTop,
    };
  }

  public applyAnchorDifferences(design: fabric.Group) {
    // Find all objects with ID containing 'anchor' and group them
    const anchorObjects = design._objects?.filter(obj => (obj as any).id?.toLowerCase().includes('anchor')) as fabric.Image[];
    if (anchorObjects && anchorObjects.length > 0) {
      const anchorGroup = new fabric.Group(this.cloneFabricObjects(anchorObjects), {
        hasControls: false,
        selectable: false,
        evented: false,
        lockMovementX: true,
        lockMovementY: true,
        flipY: true
      });

      // Scale the anchor group to match the canvas resolution
      anchorGroup.scaleToHeight(this.canvasResolution).set({
        left: 0x0,
        top: 0x0
      }).setCoords();

      design.set({
        scaleX: anchorGroup.scaleX,
        scaleY: anchorGroup.scaleY
      }).setCoords()

      const { maxLeft, maxTop } = this.findMostLeftAndTopElements(design);
      let diffX = 0;
      let diffY = 0;
      const anchorLeft = anchorObjects[0]?.left ?? 0;
      const anchorTop = anchorObjects[0]?.top ?? 0;
      // Calculate difference ensuring maxLeft/maxTop is treated as the smaller number
      if (maxLeft < 0 && anchorLeft < 0) {
        diffX = Math.abs(maxLeft - anchorLeft) * (anchorGroup.scaleX ?? 1);
      } else {
        diffX = Math.abs(anchorLeft - maxLeft) * (anchorGroup.scaleX ?? 1);
      }
      if (maxTop < 0 && anchorTop < 0) {
        diffY = Math.abs(maxTop - anchorTop) * (anchorGroup.scaleY ?? 1);
      } else {
        diffY = Math.abs(anchorTop - maxTop) * (anchorGroup.scaleY ?? 1);
      }

      // Adjust the design position to center it relative to the anchor
      const currentLeft = design.left ?? 0;
      const currentTop = design.top ?? 0;
      const designAndCanvasHeightDiff = design.getScaledHeight() - this.canvasResolution
      design.set({
        left: currentLeft - diffX,
        top: currentTop - (designAndCanvasHeightDiff - diffY)
      }).setCoords();
    }
  }

  private async addDesign() {
    const response = await fetch(this.storage_url + this.designUrl)
    const svgContent = await response.text()

    // Parse the SVG content
    const parser = new DOMParser()
    const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml')

    // Add SVG to DOM as hidden element to get dimensions
    const hiddenDiv = document.createElement('div');
    hiddenDiv.style.position = 'absolute';
    hiddenDiv.style.left = '-9999px';
    hiddenDiv.style.visibility = 'hidden';
    hiddenDiv.appendChild(svgDoc.documentElement);
    document.body.appendChild(hiddenDiv);

    if (hiddenDiv.parentNode) {
      hiddenDiv.parentNode.removeChild(hiddenDiv);
    }

    try {
      return new Promise((resolve) => {
        fabric.loadSVGFromURL(this.storage_url + this.designUrl, async (objects: any, options: any) => {
          options.crossOrigin = 'anonymous'

          this.changeColors(objects)

          const groupedObjects = this.groupObjectsBySVGHierarchy(objects, hiddenDiv.children[0] as unknown as Document);

          const img = fabric.util.groupSVGElements(groupedObjects) as fabric.Group
          img.set({
            hasControls: true,
            selectable: true,
            evented: true,
            lockMovementX: false,
            flipY: true
          })

          img.scaleToHeight(this.canvasResolution).set({
            left: 0x0,
            top: 0x0
          }).setCoords()

          this.canvas.add(img).requestRenderAll()
          img.sendBackwards()
          this.design = img

          this.applyAnchorDifferences(img)

          if (this.safeZoneUrl) {
            await this.addSafeZone()
          }
          await this.addFixedLogos()
          await this.addLogos()
          await this.addTexts()
          await this.applyAllPatterns()

          if(this.mainSizeId !== this.sizeDownload) {
            await this.reScaleLogosAndTexts()
          }

          resolve(true)
        })
      })
    } finally {
      // Clean up: remove the hidden element
      if (hiddenDiv.parentNode) {
        hiddenDiv.parentNode.removeChild(hiddenDiv);
      }
    }
  }

  private objectsOverlap(a: fabric.Object, b: fabric.Object): boolean {
    const ar = a.getBoundingRect(true, true)
    const br = b.getBoundingRect(true, true)

    return !(
      ar.left > br.left + br.width ||
      ar.left + ar.width < br.left ||
      ar.top > br.top + br.height ||
      ar.top + ar.height < br.top
    )
  }

  private pickDifference(a: number, b: number): number {
    if (a < 0 && b < 0) {
      return Math.max(a, b)
    }
    if (a > 0 && b > 0) {
      return Math.min(a, b)
    }
    // Mixed signs or one is zero: choose the value closer to zero
    return Math.abs(a) < Math.abs(b) ? a : b
  }

  private async reScaleLogosAndTexts() {
    this.canvas.getObjects().forEach((logoOrText: any) => {
      if (logoOrText.type === "logo" || logoOrText.type === "text") {
        this.design.getObjects().some((designObject: any) => {
          const isOnDesign = this.objectsOverlap(logoOrText, designObject)
          if (isOnDesign && this.sizeDifferencesRatio[designObject.id]) {
            const scaleWidthDifferences = (1 - this.sizeDifferencesRatio[designObject.id].width) * -1
            const scaleHeightDifferences = (1 - this.sizeDifferencesRatio[designObject.id].height) * -1
            const scaleDifferences = this.pickDifference(scaleWidthDifferences, scaleHeightDifferences)
            const scaleFactorX = scaleDifferences / (1 / logoOrText.scaleX)
            const scaleFactorY = scaleDifferences / (1 / logoOrText.scaleY)
            logoOrText.scaleX = logoOrText.scaleX + scaleFactorX 
            logoOrText.scaleY = logoOrText.scaleY + scaleFactorY
          }
        })
      }
    })
    this.canvas.requestRenderAll()
  }



  private groupObjectsBySVGHierarchy(objects: fabric.Object[], svgDoc: Document): fabric.Object[] {
    const groupMap = new Map<string, fabric.Object[]>();
    const idToFabricObject = new Map<string, fabric.Object>();

    // Map object IDs
    objects.forEach((obj: any) => {
      if (obj.id) idToFabricObject.set(obj.id, obj);
    });
    // Group by topmost <g> parent
    objects.forEach((obj: any) => {
      if (!obj.id) return;

      const element = svgDoc.getElementById(obj.id);
      if (!element) return;

      let parent = element.parentElement;
      let highestGroup: Element | null = null;

      while (parent && parent !== svgDoc.documentElement) {
        if (parent.tagName.toLowerCase() === 'g' && parent.id) {
          highestGroup = parent;
        }
        parent = parent.parentElement;
      }
      
      const groupID = highestGroup?.id?.toLowerCase();

      if (!groupMap.has(groupID)) {
        groupMap.set(groupID, []);
      }

      groupMap.get(groupID)!.push(obj);
    });

    // Build final grouped objects
    const groupedObjects: fabric.Object[] = [];

    groupMap.forEach((groupItems, groupID) => {
      const sizeDifferencesRatio = this.sizeDifferencesRatio[groupID]

      if (groupItems.length > 1) {
        const group = fabric.util.groupSVGElements(groupItems, {}) as fabric.Group;
        (group as any).id = groupID;

        if (sizeDifferencesRatio) {
          // keep top-left fixed (or pass 'center','center' to keep visual center)
          this.scaleKeepAnchor(
            group,
            sizeDifferencesRatio.width,
            sizeDifferencesRatio.height,
            'center',
            'center'
          );
        }

        groupedObjects.push(group);
      } else {
        const obj = groupItems[0];

        if (sizeDifferencesRatio) {
          this.scaleKeepAnchor(
            obj,
            sizeDifferencesRatio.width,
            sizeDifferencesRatio.height,
            'center',
            'center'
          );
        }

        groupedObjects.push(obj);
      }
    });

    return groupedObjects;
  }

  private scaleKeepAnchor(obj: fabric.Object, newScaleX: number, newScaleY: number, anchorX: 'left' | 'center' | 'right' = (obj.originX as any) || 'left', anchorY: 'top' | 'center' | 'bottom' = (obj.originY as any) || 'top'
  ) {
    // Early out if nothing to do
    if (obj.scaleX === newScaleX && obj.scaleY === newScaleY) return;

    // Save the current anchor point in canvas coordinates
    const anchor = obj.getPointByOrigin(anchorX, anchorY);

    // Apply scale in one set() to avoid extra layout work
    obj.set({ scaleX: newScaleX, scaleY: newScaleY });

    // Put the same anchor back to the same canvas point
    obj.setPositionByOrigin(anchor, anchorX, anchorY);

    obj.setCoords();
    obj.canvas?.requestRenderAll();
  }

  private findSizeGroupByID(id: string) {
    const index = this.size._objects.findIndex((size: any) => {
      if (size.id.indexOf('_') !== -1) {
        size.id = size.id.substring(0, size.id.indexOf('_'))
      }
      return size?.id === id
    })
    if (index !== -1) {
      return this.size._objects[index]
    }
    return null
  }

  private findMainSizeId(svgDoc: Document): string | null {
    const anchor = svgDoc.getElementById('Anchor')
    if (!anchor) return null
    let parent: Element | null = anchor.parentElement
    while (parent && parent !== svgDoc.documentElement) {
      if (parent.tagName.toLowerCase() === 'g' && parent.getAttribute('id')) {
        return parent.getAttribute('id')
      }
      parent = parent.parentElement
    }
    return null
  }


  private async addSize(size: string, is_main_size = false) {
    // Fetch SVG content manually
    const response = await fetch(this.storage_url + this.sizesUrl)
    const svgContent = await response.text()
    
    // Parse the SVG content
    const parser = new DOMParser()
    const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml')
    
    // Determine which size group to use
    let targetSizeId = size
    if (is_main_size) {
      const detectedMainId = this.findMainSizeId(svgDoc)
      if (detectedMainId) {
        targetSizeId = detectedMainId
        this.mainSizeId = detectedMainId
      }
    }

    // Find the specific size group
    const targetSizeGroup = svgDoc.querySelector(`g[id="${targetSizeId}"]`)
    if (!targetSizeGroup) {
      console.error(`Size group "${targetSizeId}" not found in SVG`)
      return Promise.resolve(false)
    }

    // Find all size groups and remove the ones we don't want
    const allGroups = svgDoc.querySelectorAll('g[id]')
    allGroups.forEach(group => {
      if (group.getAttribute('id') !== targetSizeId) {
        group.remove()
      }
    })

    const serializer = new XMLSerializer()
    const filteredSvgContent = serializer.serializeToString(svgDoc)

    const hiddenDiv = document.createElement('div');
    hiddenDiv.style.position = 'absolute';
    hiddenDiv.style.left = '-9999px';
    hiddenDiv.style.visibility = 'hidden';
    hiddenDiv.appendChild(svgDoc.documentElement);
    document.body.appendChild(hiddenDiv);
    
    const sizeSpec = {}
    hiddenDiv.children[0].children[0].childNodes.forEach((child: any) => {
      if (child.nodeType === Node.ELEMENT_NODE && child.id) {
        const id = child.id.split('_')[0].toLowerCase()
        const spec = child.getBBox?.()
        sizeSpec[id] = spec
      }
    })

    if (is_main_size) {
      this.mainSizeSpec = sizeSpec
    } else {
      this.sizeSpec = sizeSpec
    }
    // Convert back to string
    
    return new Promise((resolve) => {
      // Use loadSVGFromString with the filtered content
      fabric.loadSVGFromString(filteredSvgContent, (objects: any, options: any) => {
        options.crossOrigin = 'anonymous'
        const img = fabric.util.groupSVGElements(objects) as fabric.Group
        img.set({
          hasControls: false,
          selectable: false,
          evented: false,
          lockMovementX: true,
          lockMovementY: true,
          flipY: true
        })

        img.scaleToHeight(this.canvasResolution).set({
          left: 0x0,
          top: 0x0
        }).setCoords()

        // this.canvas.add(img).requestRenderAll()
        img.sendBackwards()

        if (is_main_size) {
          this.mainSize = img
        } else {
          this.size = img
        }

        resolve(true)
      })
    })
  }

  private calculateSizeDifferences() {
    const sizeSpec = this.sizeSpec
    const mainSizeSpec = this.mainSizeSpec

    const sizeDifferences = {}

    Object.keys(sizeSpec).forEach((key) => {
      const baseWidth = mainSizeSpec[key]?.width || 0
      const baseHeight = mainSizeSpec[key]?.height || 0
      const targetWidth = sizeSpec[key]?.width || 0
      const targetHeight = sizeSpec[key]?.height || 0

      const widthRatio = baseWidth ? targetWidth / baseWidth : 1
      const heightRatio = baseHeight ? targetHeight / baseHeight : 1

      sizeDifferences[key] = {
        width: widthRatio,
        height: heightRatio
      }
    })

    this.sizeDifferencesRatio = sizeDifferences
  }

  private async addSafeZone() {
    return new Promise((resolve) => {
      fabric.loadSVGFromURL(this.storage_url + this.safeZoneUrl, (img: any, options: any) => {
        options.crossOrigin = 'anonymous'
        const safe_zone_clip = fabric.util.groupSVGElements(img) as fabric.Group
        this.canvas.viewportCenterObject(safe_zone_clip)
        safe_zone_clip.set({
          hasControls: false,
          selectable: false,
          evented: false,
          lockMovementX: true,
          lockMovementY: true,
          absolutePositioned: true,
          inverted: true,
          flipY: true
        })

        safe_zone_clip.scaleToHeight(this.canvasResolution).set({
          left: 0x0,
          top: 0x0
        }).setCoords()

        this.safe_zone = safe_zone_clip
        resolve(true)
      })
    })
  }

  private async addLogos() {
    if (this.custom_logos && this.custom_logos.length) {
      for (const logo of this.custom_logos) {
        if (logo && logo.url) {
          await this.addLogo(logo)
        }
      }
    }
  }

  private async addLogo(logo: Record<string, any>) {
    const logoUrl = encodeURI((this.storage_url + logo.url).trim()) + '?nocache=11'
    const base64_logo = await this.convertImageURLToBase64(logoUrl) as string

    return new Promise((resolve) => {
      fabric.Image.fromURL(base64_logo, (img: any) => {
        const aspect_ratio = img.width / img.height
        if (aspect_ratio > 1) {
          img.scaleToWidth(logo.height as number * this.canvasWidthRatio)
        } else {
          img.scaleToHeight(logo.height as number * this.canvasHeightRatio)
        }
        img.set({
          left: logo.x_axis_3d? logo.x_axis_3d : logo.x_axis,
          top: logo.y_axis_3d? logo.y_axis_3d : logo.y_axis,
          angle: logo.rotation < 0 ? this.oppositeAngle(360 - logo.rotation) : this.oppositeAngle(logo.rotation),
          hasControls: false,
          selectable: false,
          evented: false,
          lockMovementX: true,
          lockMovementY: true,
          globalCompositeOperation: 'source-atop',
          originX: 'center',
          originY: 'center',
          flipX: true,
          type: "logo",
        })

        if (logo.scaleX && logo.scaleY) {
          img.scaleX = logo.scaleX * this.canvasWidthRatio
          img.scaleY = logo.scaleY * this.canvasHeightRatio
        }

        img.clipPath = this.safe_zone
        this.canvas.add(img)
        this.canvas.requestRenderAll()
        resolve(true)
      }, { crossOrigin: 'anonymous' })
    })
  }

  private oppositeAngle(angle: number): number {
    return (180 - angle + 360) % 360
  }

  private convertImageURLToBase64(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.src = url

      img.onload = () => {
        const canvas = fabric.util.createCanvasElement() as HTMLCanvasElement
        canvas.width = img.width
        canvas.height = img.height
        canvas.getContext('2d')?.drawImage(img, 0, 0)
        const dataURL = canvas.toDataURL('image/png')
        resolve(dataURL)
      }

      img.onerror = (error) => {
        reject(error)
      }
    })
  }

  public loadFont(url: string) {
    return new Promise((resolve) => {
      opentype.load(this.storage_url + url, (err: Record<any, any>, font_object: Record<any, any>) => {
        if (!err) {
          resolve(font_object);
        } else {
          resolve('')
        }
      })
    })
  }

  private async addTexts() {
    if (this.texts && this.texts.length) {
      for (const text of this.texts) {
        if (text && text.value) {
          await this.addText(text)
        }
      }
    }
  }

  private async addText(custom_text: Record<string, any>) {
    const font = await this.loadFont(custom_text.font_url) as Record<any, any>
    
    const path = font.getPath(custom_text.value, 0, 0, 72, {
      features: {
        liga: true,
        rlig: true
      }
    })

    let textSvg = '<?xml version="1.0" encoding="utf-8"?>\n' +
      '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" xml:space="preserve">\n'
    textSvg += path.toSVG()
    textSvg += '\n</svg>'

    const promises = custom_text.items.map((text: Record<any, any>) => {
      return new Promise((resolve) => {
        fabric.loadSVGFromString(textSvg, (objects: any) => {
          const fabric_text = fabric.util.groupSVGElements(objects) as fabric.Group
          fabric_text.scaleToHeight(text.height)
          fabric_text.set({
            left: text.x_axis_3d ? text.x_axis_3d : text.x_axis,
            top: text.y_axis_3d ? text.y_axis_3d : text.y_axis,
            angle: text.rotation < 0 ? this.oppositeAngle(360 - text.rotation) : this.oppositeAngle(text.rotation),
            hasControls: false,
            selectable: false,
            evented: false,
            lockMovementX: true,
            lockMovementY: true,
            globalCompositeOperation: 'source-atop',
            fill: text.color,
            stroke: text.outline_color,
            strokeWidth: parseInt(text.outline_width),
            paintFirst: 'stroke',
            lockScalingFlip: true,
            originX: 'center',
            originY: 'center',
            flipX: true,
            type: "text",
          })

          if (text.scaleX && text.scaleY) {
            fabric_text.scaleX = text.scaleX * this.canvasWidthRatio
            fabric_text.scaleY = text.scaleY * this.canvasHeightRatio
          }

          fabric_text.clipPath = this.safe_zone
          this.canvas.add(fabric_text)
          this.canvas.requestRenderAll()
          resolve(true)
        })
      })
    })

    await Promise.all(promises)
  }

  private async applyAllPatterns() {
    if (this.groupPatterns) {
      await Promise.all(
        Object.keys(this.groupPatterns).map(svg_part =>
          this.applyPattern(svg_part, false)
        )
      )
    }
  }

  private async applyPattern(svg_part: string, re_stack = true): Promise<void> {
    if (this.groupPatterns[svg_part] && this.groupPatterns[svg_part].name && this.groupPatterns[svg_part].url) {
      const pattern_data = this.groupPatterns[svg_part]

      if (pattern_data) {
        let pattern = this.patterns[svg_part]
        if (!pattern) {
          pattern = await this.setupPattern(svg_part, pattern_data, this.design)
          if (!pattern) {
            return Promise.resolve()
          }
        }

        const designObjects = this.design._objects ? this.design._objects : [this.design]
        designObjects.flat().forEach((designObject: any, index: number) => {
          designObject.set('id', designObject.id.split('_')[0])
          if (designObject.id?.toLowerCase() === svg_part) {
            const latest_design_objects = this.design._objects
            const clipGroup = new fabric.Group(
              this.cloneFabricObjects(latest_design_objects),
              {
                hasControls: false,
                selectable: false,
                evented: false,
                lockMovementX: true,
                lockMovementY: true,
                absolutePositioned: true,
                flipY: true
              }
            )

            const pattern_id = `pattern_group_${svg_part}`
            const occurrence_count = latest_design_objects.filter((obj: any) => obj.id === pattern_id).length
            const object_number = index + occurrence_count
            const objectToClip = clipGroup._objects.filter((obj: any, clip_id: number) => clip_id !== object_number)

            objectToClip.forEach((obj: any) => clipGroup.remove(obj))

            clipGroup._objects[0].clipPath = undefined
            const originalMatrix = clipGroup._objects[0].calcTransformMatrix(true)
            Object.assign(clipGroup._objects[0], {
              originalMatrix: originalMatrix
            })

            clipGroup.scaleToHeight(this.canvasResolution).set({
              left: 0x0,
              top: 0x0
            }).setCoords()

            if (occurrence_count > 0) {
              pattern = fabric.util.object.clone(pattern)
            }
            Object.assign(pattern, {
              id: `pattern_${svg_part}_${occurrence_count}`
            })

            const patternRect = new fabric.Rect({
              left: designObject.left,
              top: designObject.top,
              width: designObject.width,
              height: designObject.height,
              hasControls: false,
              selectable: false,
              evented: false,
              originX: designObject.originX || 'left',
              originY: designObject.originY || 'top',
              lockMovementX: true,
              lockMovementY: true,
              absolutePositioned: true,
              fill: pattern
            })

            patternRect.clipPath = clipGroup

            Object.assign(patternRect, {
              id: pattern_id
            })

            let design_with_rect = this.cloneFabricObjects(latest_design_objects)

            design_with_rect.splice(object_number + 1, 0, patternRect)

            this.regroupDesign(design_with_rect)

            this.patterns[svg_part] = patternRect
          }
        })

        this.canvas.requestRenderAll()
      }
    }

    return Promise.resolve()
  }

  private regroupDesign(design_objects: fabric.Object[]) {
    const final_design = new fabric.Group(design_objects, {
      hasControls: false,
      selectable: false,
      evented: false,
      lockMovementX: true,
      lockMovementY: true,
      flipY: true
    })
    final_design.scaleToHeight(this.canvasResolution).set({
      left: 0x0,
      top: 0x0
    }).setCoords()

    this.canvas.remove(this.design)
    this.canvas.add(final_design)
    final_design.sendToBack()

    this.applyAnchorDifferences(final_design)

    this.design = final_design
  }

  public generateProductionSVG(targetWidth: number, targetHeight: number): string {
    const matrixReplacements = new Map<string, string>()

    this.design.getObjects().flat().forEach((designObject: any) => {
      if (designObject.id?.includes('pattern_') && designObject.clipPath) {
        const clipObject = designObject.clipPath._objects[0]

        const currentMatrix = clipObject.calcTransformMatrix()
          .map(n => roundOff(n)).join(' ')
        const originalMatrix = clipObject.originalMatrix
          .map(n => roundOff(n)).join(' ')

        matrixReplacements.set(currentMatrix, originalMatrix)
      }
    })

    fabric.Object.NUM_FRACTION_DIGITS = 6;
    let svg = this.canvas.toSVG()

    svg = svg.replace(/^\s*(<\?xml[^>]*>\s*)?(<!DOCTYPE[^>]*>\s*)?/i, '')

    // Ensure root width/height/viewBox reflect target dimensions
    // width
    if (/width="[^"]+"/i.test(svg)) {
      svg = svg.replace(/(width=")([^"]+)(")/i, `$1${targetWidth}$3`)
    } else {
      svg = svg.replace(/(<svg)(\s)/i, `$1 width="${targetWidth}"$2`)
    }
    // height
    if (/height="[^"]+"/i.test(svg)) {
      svg = svg.replace(/(height=")([^"]+)(")/i, `$1${targetHeight}$3`)
    } else {
      svg = svg.replace(/(<svg)(\s)/i, `$1 height="${targetHeight}"$2`)
    }
    // // viewBox
    // if (/viewBox="[^"]+"/i.test(svg)) {
    //   svg = svg.replace(/(viewBox=")([^"]+)(")/i, `$1${`0 0 ${targetWidth} ${targetHeight}`}$3`)
    // } else {
    //   svg = svg.replace(/(<svg[^>]*)(>)/i, `$1 viewBox="0 0 ${targetWidth} ${targetHeight}"$2`)
    // }

    matrixReplacements.forEach((original, current) => {
      const safePattern = current.replace(/\./g, '\\.').replace(/-/g, '\\-')
      const regex = new RegExp(`matrix\\(${safePattern}\\)`, 'g')
      svg = svg.replace(regex, `matrix(${original})`)
    })

    svg = svg.replace(
      /(<svg[^>]*>)/i,
      `$1<g transform="scale(1,-1) translate(0, -${this.canvasResolution})">`
    ).replace(/<\/svg>/i, '</g></svg>')

    Object.keys(this.patterns).forEach((svg_part) => {
      const angle = this.groupPatterns[svg_part]?.angle
      if (angle) {
        const patternIdRegex = new RegExp(
          `<pattern([^>]+id="SVGID_pattern_${svg_part}_\\d+"[^>]*)>`,
          'g'
        )

        svg = svg.replace(patternIdRegex, (match, attrs) => {
          if (/patternTransform\s*=/.test(attrs)) {
            return `<pattern ${attrs.replace(/patternTransform="[^"]*"/, `patternTransform="rotate(${angle})"`)}>`
          } else {
            return `<pattern ${attrs} patternTransform="rotate(${angle})">`
          }
        })
      }
    })

    return svg
  }
  public svgToDataURL(svgElement) {
    const svgString = new XMLSerializer().serializeToString(svgElement);
    const encodedData = btoa(unescape(encodeURIComponent(svgString)));
    return `data:image/svg+xml;base64,${encodedData}`;
  }
}
